import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import CTAButton from '../CTAButton';

import itImg from '../../Assest/cards/home/2/IT & Software.avif';
import healthcareImg from '../../Assest/cards/home/2/Healthcare.avif';
import manufacturingImg from '../../Assest/cards/home/2/Manufacturing.avif';
import bfsiImg from '../../Assest/cards/home/2/BFSI.avif';
import ecommerceImg from '../../Assest/cards/home/2/Ecommerce.avif';
import logisticsImg from '../../Assest/cards/home/2/Logistics.avif';
import startupsImg from '../../Assest/cards/home/2/Startups.avif';
import gccsImg from '../../Assest/cards/home/2/GCCs.avif';

const industries = [
    { name: "IT & Software", description: "Build high-performing tech teams with our specialized recruitment systems, connecting you to top software engineers, architects, and engineering leaders who drive innovation and digital transformation.", slug: "/industries/information-technology", bgImg: itImg },
    { name: "Healthcare", description: "Secure certified medical practitioners, nursing staff, clinical specialists, and executive administrative leaders to ensure seamless operational continuity and high-quality patient care standards.", slug: "/industries/healthcare", bgImg: healthcareImg },
    { name: "Manufacturing", description: "Optimize production lines, quality assurance, and operations with experienced manufacturing engineers, plant managers, and supply chain leadership specialists tailored for modern industrial systems.", slug: "/industries/manufacturing", bgImg: manufacturingImg },
    { name: "BFSI", description: "Acquire specialized finance, investment banking, risk management, and insurance compliance professionals equipped to navigate modern financial landscapes and regulatory frameworks.", slug: "/industries/bfsi", bgImg: bfsiImg },
    { name: "Ecommerce", description: "Drive digital commerce sales and operations with skilled product managers, digital marketing strategists, and supply chain logistics talent optimized for rapid direct-to-consumer scalability.", slug: "/industries/ecommerce", bgImg: ecommerceImg },
    { name: "Logistics", description: "Accelerate warehousing, fleet management, and global distribution operations by hiring experienced supply chain coordinators, logistics specialists, and transport managers.", slug: "/industries/logistics", bgImg: logisticsImg },
    { name: "Startups", description: "Scale your venture from seed to expansion stage with agile, versatile builders, multi-functional experts, and growth-focused leaders who thrive in fast-paced startup environments.", slug: "/industries/startups", bgImg: startupsImg },
    { name: "GCCs", description: "Establish, scale, and optimize world-class Global Capability Centers (GCCs) with strategic local leadership, specialized engineering talent, and operational hubs aligned to global standards.", slug: "/industries/gcc", bgImg: gccsImg },
];

const OrbitalCard = ({ item, idx, totalCards, smoothTotalRot, radius, navigate, isMobile }) => {
    // The angle for this card in the circle
    const baseAngle = (360 / totalCards) * idx;

    // Current angle based on scroll + drag
    const angle = useTransform(smoothTotalRot, (rot) => baseAngle + rot);

    // Calculate x and y on the circle border
    // Centered at 50%, 50% using calc offset
    const x = useTransform(angle, (a) => `calc(${Math.sin(a * Math.PI / 180) * radius}px - 50%)`);
    const y = useTransform(angle, (a) => `calc(${-Math.cos(a * Math.PI / 180) * radius}px - 50%)`);

    // Scale down cards that are at the bottom of the circle (further away)
    const scale = useTransform(angle, (a) => {
        const normalized = ((a % 360) + 360) % 360;
        let dist = Math.min(normalized, 360 - normalized);
        return 1 - (dist / 180) * 0.3;
    });

    // Fade out cards at the bottom
    const opacity = useTransform(angle, (a) => {
        const normalized = ((a % 360) + 360) % 360;
        let dist = Math.min(normalized, 360 - normalized);
        return 1 - (dist / 180) * 0.8;
    });

    const zIndex = useTransform(angle, (a) => {
        const normalized = ((a % 360) + 360) % 360;
        let dist = Math.min(normalized, 360 - normalized);
        return 100 - Math.round(dist);
    });

    return (
        <motion.div
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                x,
                y,
                scale,
                opacity,
                zIndex
            }}
            onClick={(e) => {
                e.stopPropagation();
                navigate(item.slug);
            }}
            className={`cursor-pointer ${isMobile ? 'w-[290px] h-[180px]' : 'w-[450px] h-[250px]'} rounded-[2.5rem]`}
        >
            <div
                style={{
                    backgroundImage: `linear-gradient(to bottom, rgba(1, 1, 76, 0.4), rgba(1, 1, 76, 0.85)), url('${item.bgImg}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
                className={`w-full h-full flex flex-col justify-between p-6 md:p-8 rounded-[2.5rem] border-2 border-[#3b82f6]/50 text-white shadow-[0_30px_60px_rgba(59,130,246,0.3)] transition-all duration-500 hover:scale-[1.02]`}
            >
                <div className="flex flex-col gap-2">
                    <h4 className="text-xl md:text-2xl font-bold font-sans tracking-wide leading-tight">
                        {item.name}
                    </h4>
                    <p className="text-white/80 text-xs md:text-sm leading-relaxed font-medium mt-1 line-clamp-3">
                        {item.description}
                    </p>
                </div>

                <div className="flex items-center gap-2 text-sm font-black uppercase tracking-wider transition-all duration-500 text-blue-400">
                    <span>Learn More</span>
                    <ChevronRight size={16} />
                </div>
            </div>
        </motion.div>
    );
};

const IndustryConsulting = () => {
    const containerRef = useRef(null);
    const navigate = useNavigate();

    // Scroll progress from top of the 250vh section to the bottom
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const [radius, setRadius] = useState(750);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 768);
            // Dynamic radius based on screen size
            setRadius(width < 768 ? 320 : width < 1024 ? 500 : 750);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 0 to 1 scroll -> 0 to 360 degrees rotation
    const scrollRot = useTransform(scrollYProgress, [0, 1], [0, 360]);
    const dragOffset = useMotionValue(0);

    // Combine scroll rotation and drag rotation
    const totalRot = useTransform(() => scrollRot.get() + dragOffset.get());

    // Smooth out the movement with a spring
    const smoothTotalRot = useSpring(totalRot, { stiffness: 80, damping: 20 });

    const handleDrag = (e, info) => {
        // Drag horizontally directly affects rotation angle
        dragOffset.set(dragOffset.get() + info.delta.x * 0.5);
    };

    const handleDragEnd = (e, info) => {
        const current = scrollRot.get() + dragOffset.get();
        // Add velocity for swipe momentum
        const target = current + info.velocity.x * 0.2;
        // Snap to nearest 45 degrees (360 / 8 items)
        const snapped = Math.round(target / 45) * 45;
        dragOffset.set(snapped - scrollRot.get());
    };

    return (
        <section
            id="industry-consulting"
            ref={containerRef}
            style={{
                height: '250vh',
                position: 'relative',
                backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95)), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}
        >
            <div style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

                {/* Section Title Pinned to Top */}
                <div className="absolute top-[5%] md:top-[8%] text-center z-20 px-6 w-full max-w-4xl mx-auto pointer-events-none">
                    <motion.span
                        initial={{ opacity: 0, y: -10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-blue-600 font-bold tracking-[0.3em] text-[10px] md:text-xs uppercase block mb-3"
                    >
                        Market Verticals
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-3xl md:text-5xl font-bold text-slate-900 syne leading-tight mb-4"
                    >
                        Industry-Focused <br />
                        <span className="text-blue-600">HR Consulting Services</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-slate-500 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed"
                    >
                        Every sector has a unique DNA. Our specialized teams bridge the gap between complex industry requirements and top-tier talent.
                    </motion.p>
                </div>

                {/* Orbital Carousel Ring */}
                <motion.div
                    onPan={handleDrag}
                    onPanEnd={handleDragEnd}
                    style={{
                        position: 'absolute',
                        top: `calc(55vh + ${radius}px)`,
                        left: '50%',
                        width: '100vw',
                        height: `${radius * 2}px`,
                        transform: 'translate(-50%, -50%)',
                        touchAction: 'pan-y',
                        cursor: 'grab'
                    }}
                    whileTap={{ cursor: 'grabbing' }}
                >
                    {industries.map((item, idx) => (
                        <OrbitalCard
                            key={item.slug}
                            item={item}
                            idx={idx}
                            totalCards={industries.length}
                            smoothTotalRot={smoothTotalRot}
                            radius={radius}
                            navigate={navigate}
                            isMobile={isMobile}
                        />
                    ))}
                </motion.div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none z-20 opacity-60">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Scroll to Orbit</span>
                    <div className="w-px h-12 bg-gradient-to-b from-slate-400 to-transparent"></div>
                </div>

            </div>
        </section>
    );
};

export default IndustryConsulting;